import {
  Box,
  Container,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { offerService } from '../services/offerService';
import offerDetailService from '@/features/offer-detail/services/offerDetailService';
import { useSnackbar } from '@/shared/components/notifications';
import { LocationSection } from './LocationSection';
import { ProductSection } from './ProductSection';
import { AppButton } from '@/shared/components';
import { AvailabilitySection } from './AvailabilitySection';
import { DocumentSection } from './DocumentSection';
import { TitleSection } from './TitleSection';
import { DescriptionSection } from './DescriptionSection';
import { OfferFormData, ProductFormData } from '../types/offer.types';

interface ExistingPhoto {
  id: string;
  url: string;
  thumbnailUrl?: string;
}

interface ExistingDocument {
  id: string;
  filename: string;
  fileSize: number;
  url: string;
}

export function EditOfferPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showSuccess, showError } = useSnackbar();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [existingPhotos, setExistingPhotos] = useState<ExistingPhoto[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<ExistingDocument[]>([]);
  const [originalStartDate, setOriginalStartDate] = useState<Date | undefined>(undefined);

  const defaultProduct: ProductFormData = {
    materialId: '',
    materialCategory: '',
    materialSubcategory: '',
    unitPrice: '',
    quantity: '',
    unitId: '',
    condition: 'used',
    photos: [],
  };

  const methods = useForm<OfferFormData>({
    defaultValues: {
      title: '',
      description: '',
      address: '',
      products: [defaultProduct],
      startDate: new Date(),
      endDate: null,
      documents: [],
    },
    mode: 'onChange',
  });

  // Load the existing offer and pre-fill the form
  useEffect(() => {
    if (!id) {
      setLoadError('Offer ID is missing');
      setIsLoading(false);
      return;
    }

    const load = async () => {
      try {
        setIsLoading(true);
        const offer = await offerDetailService.getOfferById(id);
        const product = offer.products[0];

        methods.reset({
          title: offer.title || '',
          description: offer.description || '',
          address: offer.address || '',
          latitude: offer.latitude,
          longitude: offer.longitude,
          products: [
            {
              materialId: product?.material?.id || '',
              materialCategory: product?.material?.category || '',
              materialSubcategory: product?.material?.subcategory || '',
              unitPrice: product ? String(product.unitPrice) : '',
              quantity: product ? String(product.quantity) : '',
              unitId: product?.unit?.id || '',
              condition: product?.status === 'NEW' ? 'new' : 'used',
              photos: [],
            },
          ],
          startDate: offer.startDate ? new Date(offer.startDate) : new Date(),
          endDate: offer.endDate ? new Date(offer.endDate) : null,
          documents: [],
        });

        setOriginalStartDate(offer.startDate ? new Date(offer.startDate) : undefined);

        setExistingPhotos(
          (product?.photos || []).map(p => ({
            id: p.id,
            url: p.url,
            thumbnailUrl: undefined,
          }))
        );

        setExistingDocuments(
          (offer.documents || []).map(d => ({
            id: d.id,
            url: d.url,
            filename: d.filename,
            fileSize: d.fileSize,
          }))
        );

        // Force re-validation after reset to ensure isValid is correct on iOS
        await methods.trigger();

        setLoadError(null);
      } catch (err) {
        console.error('Failed to load offer for editing:', err);
        setLoadError(t('publish.edit.loadError'));
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [id, methods, t]);

  const handleDeleteExistingPhoto = (photoId: string) => {
    setExistingPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const handleDeleteExistingDocument = (docId: string) => {
    setExistingDocuments(prev => prev.filter(d => d.id !== docId));
  };

  const toLocalDateISO = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}T00:00:00.000Z`;
  };

  const onSubmit = async (data: OfferFormData) => {
    if (!id) return;

    try {
      setIsSubmitting(true);

      const product = data.products[0] ?? defaultProduct;

      const payload = {
        title: data.title,
        description: data.description,
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        startDate: toLocalDateISO(data.startDate ?? new Date()),
        endDate: toLocalDateISO(data.endDate ?? new Date()),
        materialId: product.materialId,
        materialCategory: product.materialCategory,
        materialSubcategory: product.materialSubcategory,
        unitPrice: product.unitPrice ? Number.parseFloat(product.unitPrice) : undefined,
        quantity: product.quantity ? Number.parseFloat(product.quantity) : undefined,
        unitId: product.unitId,
        productStatus: product.condition === 'new' ? ('NEW' as const) : ('USED' as const),
      };

      await offerService.updateOffer(
        id,
        payload,
        product.photos || [],
        data.documents || [],
        existingPhotos.map(p => p.id),
        existingDocuments.map(d => d.id)
      );

      showSuccess(t('publish.edit.successMessage'));
      navigate(`/offers/${id}`, { state: { from: 'my-publications' }, replace: true });
    } catch (error) {
      console.error('Error updating offer:', error);
      showError(error instanceof Error ? error.message : t('publish.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  const { isValid } = methods.formState;

  return (
    <Box sx={{ pb: 10, pt: 0 }}>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: 'primary.main',
          borderRadius: '0 0 16px 16px',
          zIndex: theme => theme.zIndex.appBar,
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            aria-label={t('common.back')}
            data-testid="edit-offer-back-button"
          >
            <ArrowBack />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{ color: 'white', fontWeight: 500, flex: 1, textAlign: 'center' }}
          >
            {t('publish.edit.title')}
          </Typography>
          {/* Spacer to keep title centered */}
          <Box sx={{ width: 48 }} />
        </Toolbar>
      </AppBar>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <TitleSection />

            <LocationSection />

            <ProductSection
              index={0}
              existingPhotos={existingPhotos}
              onDeleteExistingPhoto={handleDeleteExistingPhoto}
            />

            <DescriptionSection />

            <AvailabilitySection isEditMode originalStartDate={originalStartDate} />

            <DocumentSection
              existingDocuments={existingDocuments}
              onDeleteExistingDocument={handleDeleteExistingDocument}
            />

            <AppButton
              type="submit"
              variant="primary"
              fullWidth
              disabled={!isValid}
              loading={isSubmitting}
              data-testid="edit-offer-submit-button"
            >
              {isSubmitting ? t('publish.submitting') : t('publish.edit.submitButton')}
            </AppButton>
          </Box>
        </FormProvider>
      </Container>
    </Box>
  );
}
